import { ReactNode } from 'react';

import { Card, CardContent } from '@splitfiesta/ui';

// Base skeleton component
export function Skeleton({
  className = '',
  ...props
}: {
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={`animate-pulse bg-gray-200 dark:bg-gray-700 rounded-sm ${className}`}
      {...props}
    />
  );
}

// Standard skeleton sizes
export function SkeletonText({
  className = '',
  size = 'base',
}: {
  className?: string;
  size?: 'sm' | 'base' | 'lg' | 'xl';
}) {
  const heights = {
    sm: 'h-3',
    base: 'h-4',
    lg: 'h-6',
    xl: 'h-8',
  };

  return <Skeleton className={`${heights[size]} ${className}`} />;
}

export function SkeletonTitle({ className = '' }: { className?: string }) {
  return <Skeleton className={`h-6 ${className}`} />;
}

export function SkeletonButton({ className = '' }: { className?: string }) {
  return <Skeleton className={`h-10 ${className}`} />;
}

// Page header skeleton
export function SkeletonPageHeader({
  hasButton = false,
}: {
  hasButton?: boolean;
}) {
  return (
    <div className="flex justify-between items-center">
      <div>
        <SkeletonTitle className="w-48 mb-2" />
        <SkeletonText className="w-64" />
      </div>
      {hasButton && <SkeletonButton className="w-32" />}
    </div>
  );
}

// Stats card skeleton
export function SkeletonStatCard() {
  return (
    <Card>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Skeleton className="w-10 h-10 rounded-full" />
          <div>
            <SkeletonText className="w-20 mb-1" />
            <SkeletonText className="w-16" size="sm" />
          </div>
        </div>
        <div className="text-right">
          <SkeletonText className="w-16 mb-1" size="lg" />
          <SkeletonText className="w-12" size="sm" />
        </div>
      </div>
    </Card>
  );
}

// Group card skeleton
export function SkeletonGroupCard() {
  return (
    <Card>
      <CardContent className="px-6 py-4">
        <div className="flex flex-col space-y-3">
          {/* Top section: Avatar, Name, Description and Balance */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start space-x-3 flex-1 min-w-0">
              <Skeleton className="w-10 h-10 sm:w-12 sm:h-12 rounded-full shrink-0" />
              <div className="flex-1 min-w-0">
                <SkeletonTitle className="w-32 mb-1" />
                <SkeletonText className="w-48" size="sm" />
              </div>
            </div>
            <div className="flex flex-col items-end space-y-1 shrink-0">
              <Skeleton className="w-8 h-4 rounded-md" />
              <div className="text-right">
                <SkeletonText className="w-16 mb-1" size="lg" />
                <SkeletonText className="w-12" size="sm" />
              </div>
            </div>
          </div>

          {/* Bottom section: Members, Expenses, and Date */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="flex items-center space-x-1">
                <Skeleton className="w-4 h-4" />
                <SkeletonText className="w-16" size="sm" />
              </div>
              <div className="flex items-center space-x-1">
                <Skeleton className="w-4 h-4" />
                <SkeletonText className="w-20" size="sm" />
              </div>
            </div>
            <div className="flex items-center space-x-1">
              <Skeleton className="w-4 h-4" />
              <SkeletonText className="w-24" size="sm" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Expense item skeleton
export function SkeletonExpenseItem() {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <SkeletonTitle className="w-48" />
              <Skeleton className="w-16 h-6 rounded-full" />
            </div>
            <SkeletonText className="w-64 mb-3" />
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-1">
                <Skeleton className="w-4 h-4" />
                <SkeletonText className="w-20" size="sm" />
              </div>
              <div className="flex items-center space-x-1">
                <Skeleton className="w-4 h-4" />
                <SkeletonText className="w-16" size="sm" />
              </div>
              <SkeletonText className="w-24" size="sm" />
            </div>
          </div>
          <div className="text-right">
            <SkeletonText className="w-20 mb-1" size="lg" />
            <SkeletonText className="w-16" size="sm" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Member item skeleton
export function SkeletonMemberItem() {
  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
      <div className="flex items-center space-x-3">
        <Skeleton className="w-12 h-12 rounded-full" />
        <div>
          <SkeletonText className="w-24 mb-1" />
          <SkeletonText className="w-32" size="sm" />
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <SkeletonText className="w-16" size="sm" />
        <Skeleton className="w-8 h-8 rounded-sm" />
      </div>
    </div>
  );
}

// Form field skeleton
export function SkeletonFormField() {
  return (
    <div className="space-y-2">
      <SkeletonText className="w-24" size="sm" />
      <Skeleton className="w-full h-10 rounded-sm" />
    </div>
  );
}

// Card skeleton wrapper
export function SkeletonCard({
  children,
  className = '',
  ...props
}: {
  children: ReactNode;
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`card ${className}`} {...props}>
      {children}
    </div>
  );
}

// Filter buttons skeleton
export function SkeletonFilters({ count = 3 }: { count?: number }) {
  return (
    <div className="flex items-center space-x-4">
      <div className="flex items-center space-x-2">
        <Skeleton className="w-5 h-5" />
        <SkeletonText className="w-12" size="sm" />
      </div>
      <div className="flex space-x-2">
        {Array.from({ length: count }).map((_, i) => (
          <Skeleton key={i} className="w-16 h-8 rounded-full" />
        ))}
      </div>
    </div>
  );
}

// Grid layout skeleton
export function SkeletonGrid({
  columns = 3,
  count = 6,
  children,
}: {
  columns?: number;
  count?: number;
  children: ReactNode;
}) {
  const gridClass =
    {
      1: 'grid-cols-1',
      2: 'grid-cols-1 md:grid-cols-2',
      3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
      4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
    }[columns] || 'grid-cols-3';

  return (
    <div className={`grid ${gridClass} gap-6`}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i}>{children}</div>
      ))}
    </div>
  );
}

// Page layout skeleton
export function SkeletonPageLayout({
  header,
  content,
  sidebar,
}: {
  header?: ReactNode;
  content: ReactNode;
  sidebar?: ReactNode;
}) {
  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {header}
      <div className={sidebar ? 'grid grid-cols-1 lg:grid-cols-2 gap-8' : ''}>
        <div className={sidebar ? 'space-y-6' : ''}>{content}</div>
        {sidebar && <div className="space-y-6">{sidebar}</div>}
      </div>
    </div>
  );
}
